import { fetchFromVolatileServer } from "../../utils/fetchFromVolatileServer";
import { getUrl } from "../../utils/url";
// import { PostTypes } from "../types/client";
import {
  Post,
  PostTypes,
} from "@aleksundshantu/schirdewan-standard-types/client";
import {
  applyCustomParams,
  applyFields,
  checkMaxPostsPerFetch,
  removePageParam,
} from "./helpers";
import {
  CustomParams,
  FetchGenericEndpointProps,
  GetEventsProps,
  GetGenericEndpointProps,
  GetPostsProps,
  WPClientProps,
} from "./types";
import { DeepKey, ExtractPaths } from "./utility-types";

type EventPostType = Post<"events">;

export interface CustomEndpoint<PostType> {
  posts: PostType;
  totalPosts: number;
  events_before: number;
  events_after: number;
}

export class WPClient {
  private locale: string | undefined;
  private defaultLocale: string | undefined;

  private maxPostsPerFetch: number = 50;

  constructor({ locale, defaultLocale, maxPostsPerFetch }: WPClientProps) {
    this.locale = locale;
    this.defaultLocale = defaultLocale;
    if (maxPostsPerFetch) {
      checkMaxPostsPerFetch(maxPostsPerFetch);
      this.maxPostsPerFetch = maxPostsPerFetch;
    }
  }

  public setLocale(locale: string) {
    this.locale = locale;
  }
  public setDefaultLocale(defaultLocale: string) {
    this.defaultLocale = defaultLocale;
  }

  public setLocaleAndDefaultLocale(locale: string, defaultLocale: string) {
    this.locale = locale;
    this.defaultLocale = defaultLocale;
  }

  public setMaxPostsPerFetch(maxPostsPerFetch: number) {
    checkMaxPostsPerFetch(maxPostsPerFetch);
    this.maxPostsPerFetch = maxPostsPerFetch;
  }

  /**
   *
   * @param includeIds to only return posts with ids inside the includeIds.ids array
   * if you want to keep the order of those ids - which might be wanted if they can be selected in the backend and the author wants to have control over the order -
   * set includeIds.keepOrder to true
   */
  async fetchGenericEndpoint<T, FieldsArray extends DeepKey<T>[]>({
    endpoint,
    page = 1,
    perPage = this.maxPostsPerFetch,
    offset,
    slug,
    fields,
    customParams = {},
    includeIds = { ids: [], keepOrder: false },
    excludeIds,
    orderBy,
    order,
    fetchAll = false,
    abortController,
    includeCategories,
  }: FetchGenericEndpointProps<T, FieldsArray>) {
    //build url params
    const urlParams = new URLSearchParams();
    urlParams.append("per_page", perPage.toString());
    urlParams.append("page", page.toString());
    if (offset) urlParams.append("offset", offset.toString());
    if (includeIds) {
      urlParams.append("include", includeIds.ids.join(","));
      if (includeIds.keepOrder) orderBy = "include";
    }
    if (excludeIds) {
      urlParams.append("exclude", excludeIds.join(","));
    }
    if (slug) urlParams.append("slug", slug.toString());
    if (order) urlParams.append("order", order);
    if (orderBy) urlParams.append("orderby", orderBy);
    if (includeCategories) {
      for (let index = 0; index < includeCategories.length; index++) {
        const cat = includeCategories[index];
        if (cat.ids.length > 0) {
          urlParams.append(cat.category, cat.ids.join(","));
        }
      }
    }

    applyFields<T, FieldsArray>(urlParams, fields);
    applyCustomParams(customParams, urlParams);

    //fetch
    let url =
      getUrl(this.locale, this.defaultLocale) +
      `/${endpoint}?_embed=wp:term&${urlParams}`;
    // console.log(`Fetching ${endpoint} from ${url}`);
    const res = await fetchFromVolatileServer(url, {
      signal: abortController?.signal,
    });
    const totalPosts = res.headers.get("X-Wp-Total");
    if (!totalPosts) {
      throw new Error("X-WP-Total header missing while fetching " + url);
    }
    if (isNaN(Number(totalPosts))) {
      throw new Error("X-WP-Total header should be a number");
    }
    const json = await res.json();

    if (fetchAll && json.length < Number(totalPosts)) {
      const remainingPosts = Number(totalPosts) - json.length;
      const remainingPages = Math.ceil(remainingPosts / perPage);
      for (let i = 1; i <= remainingPages; i++) {
        // remove page from url and add page + i
        url = removePageParam(url, "page");
        const res = await fetchFromVolatileServer(`${url}&page=${page + i}`, {
          signal: abortController?.signal,
        });
        const additionalPosts = await res.json();
        json.push(...additionalPosts);
      }
    }
    return { data: json, totalPosts: Number(totalPosts) };
  }

  async getGenericEndpoint<T, FieldsArray extends DeepKey<T>[]>({
    limit,
    ...props
  }: GetGenericEndpointProps<T, FieldsArray>): Promise<{
    //TODO: type this
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customData: any;
    elements: FieldsArray extends []
      ? T[] //if no fields are specified return full post type
      : ExtractPaths<T, FieldsArray>[];
    totalPosts: number;
  }> {
    if (props.page && limit) {
      throw new Error("if page is set, please use perPage as limit");
    }
    if (!props.page && props.perPage) {
      throw new Error(
        "perPage can only be used in combination with page - if you want to limit the amount of posts returned and dont want to set a specific page, please use the limit prop",
      );
    }
    let alreadyFetched = 0;
    let currentPage = props.page || 1;
    let totalPosts: number | undefined;
    const elements: typeof props.fields extends []
      ? T[]
      : ExtractPaths<T, FieldsArray>[] = [];
    let customData;
    while (
      !totalPosts || //first fetch
      alreadyFetched < totalPosts //prevent fetching more posts than created in backend
    ) {
      if (limit && alreadyFetched >= limit) {
        //prevent fetching more posts than limit if provided
        break;
      }
      let perPage = props.perPage || this.maxPostsPerFetch;
      if (limit && currentPage * perPage > limit) {
        // if limit is set and this loop would fetch more posts than limit based on maxPostsPerFetch
        // only fetch the reamining posts
        perPage = limit - alreadyFetched;
      }

      const result = await this.fetchGenericEndpoint<T, FieldsArray>({
        ...props,
        fields: props.fields,
        page: currentPage,
        perPage,
      });

      if (!totalPosts) totalPosts = result.totalPosts;
      if (Array.isArray(result.data)) {
        elements.push(...result.data);
      } else if (result.data.results) {
        elements.push(...result.data.results);
        if (!customData) {
          const { results: _, ...rest } = result.data;
          customData = rest;
        }
      } else {
        throw new Error("Unexpected response format from endpoint");
      }

      if (props.page || totalPosts === 0) {
        //if a specific page is set or totalPosts is 0, we break the while loop after the first fetch
        break;
      }
      alreadyFetched += perPage;
      currentPage++;
    }
    if (!totalPosts) {
      totalPosts = 0;
    }
    return {
      elements,
      customData,
      totalPosts,
    };
  }

  /**
   * Updates the WPML translations with corrected URLs based on post type and locale.
   */
  public async fixWPMLMetaData(
    pageData: Post | null,
    isStartPage?: boolean,
  ): Promise<Post | null> {
    if (!pageData || !pageData.wpml_translations?.length) {
      return pageData;
    }
    const postTypeString = pageData?.type;
    const type = postTypeString === "page" ? "pages" : postTypeString;
    for (const translation of pageData.wpml_translations) {
      const translatedPageId = translation.id;

      if (!translatedPageId) continue;
      const localeToTranslate = translation.locale.substring(0, 2);

      if (postTypeString && !isStartPage) {
        const result = await this.getPosts({
          type: type as PostTypes,
          includeIds: { ids: [translatedPageId] },
          fields: ["link"], // Fetch only the link field
          perPage: 1, // We only need one post
          page: 1,
        });

        if (result.posts?.length) {
          translation.href = result.posts[0].link;
        }
      } else if (isStartPage) {
        // Handle start page
        translation.href =
          localeToTranslate === this.defaultLocale
            ? "/"
            : `/${localeToTranslate}/`;
      }
    }
    return pageData;
  }

  public async getPosts<
    P extends Post<PostTypeString>,
    FieldsArray extends DeepKey<P>[],
    PostTypeString extends PostTypes,
  >({
    type,
    includeTaxonomies,
    ...props
  }: GetPostsProps<P, FieldsArray, PostTypeString>) {
    const taxonomyParams: CustomParams = {};
    for (const taxonomy of includeTaxonomies ?? []) {
      taxonomyParams[taxonomy.taxonomy] = taxonomy.ids.join(",");
    }

    const res = await this.getGenericEndpoint<P, FieldsArray>({
      endpoint: type,
      customParams: {
        ...props.customParams,
        ...taxonomyParams,
      },
      ...props,
    });

    const { elements, ...rest } = res;

    return {
      ...rest,
      posts: elements,
    };
  }

  // Overload signatures
  public getEvents<FieldsArray extends DeepKey<EventPostType>[] = []>(
    params: GetEventsProps<EventPostType, FieldsArray> & {
      startDate?: undefined;
      endDate?: undefined;
    },
  ): Promise<
    FieldsArray extends []
      ? Post<"events">[] //if no fields are specified return full post type
      : ExtractPaths<Post<"events">, FieldsArray>[]
  >;

  public getEvents<FieldsArray extends DeepKey<EventPostType>[] = []>(
    params: GetEventsProps<EventPostType, FieldsArray>,
  ): Promise<
    CustomEndpoint<
      FieldsArray extends []
        ? Post<"events">[] //if no fields are specified return full post type
        : ExtractPaths<Post<"events">, FieldsArray>[]
    >
  >;

  // Implementation
  public async getEvents<FieldsArray extends DeepKey<EventPostType>[] = []>({
    fields,
    startDate,
    endDate,
    excludeIds,
  }: GetEventsProps<EventPostType, FieldsArray>) {
    const result = await this.getPosts({
      type: "events",
      fields,
      customParams: {
        ...(startDate && {
          start_date: startDate.toISOString().split(".")[0],
        }),
        ...(endDate && {
          end_date: endDate.toISOString().split(".")[0],
        }),
        ...(excludeIds && {
          exclude: excludeIds.join(","),
        }),
      },
    });
    if (result.customData) {
      return {
        posts: result.posts,
        ...result.customData,
        totalPosts: result.totalPosts,
      };
    }
    return result.posts;
  }
}
